"use strict";

/**
 * The four `mobConfig` booking offsets do NOT share a unit (MOB-050).
 *
 * - `schoolBookingOffset`, `schoolGroupBookingOffset`, `schoolCampBookingOffset` are MINUTES
 *   (decided 2026-08-05, MOB-005) and are read through `DateUtil.bookingMinDateFromMinutes`.
 * - The rental offset is HOURS (decided 2026-08-22, MOB-050) and is read through
 *   `DateUtil.bookingMinDate`.
 *
 * Same shape, same config object, two units — so the rental field carries its unit in its
 * NAME: `rentBookingOffsetHours`. A shop that copies its school value (`120`) into the rental
 * field is asking for 120 HOURS = five days of lead time, and the field name is now the only
 * warning it gets before every rental for five days disappears from the catalogue.
 *
 * `rentBookingOffset` is the pre-rename spelling and is still accepted as a FALLBACK: a
 * MOBConfig row published before the rename carries only that key, and dropping it would
 * silently reset such a tenant's lead time to zero. Remove the fallback (and the schema
 * field) once every tenant's MOBConfig has been republished through
 * `MssToMobTransform`, which writes both names.
 *
 * Read the offset ONLY through this accessor. A call site that reaches into `mobConfig`
 * itself re-opens the defect: it takes a number whose unit is not visible at the call site.
 */
class BookingOffsetUtil {
  /** Current, unit-carrying MOBConfig field name. */
  static get RENT_OFFSET_FIELD() {
    return "rentBookingOffsetHours";
  }

  /** Pre-rename field name, still read as a fallback. */
  static get RENT_OFFSET_LEGACY_FIELD() {
    return "rentBookingOffset";
  }

  /**
   * Rental booking lead time in HOURS.
   *
   * @param {Object|null|undefined} mobConfig
   * @returns {number} hours; 0 when the tenant configured no offset
   */
  static rentOffsetHours(mobConfig) {
    const config = mobConfig || {};
    const configured =
      config[BookingOffsetUtil.RENT_OFFSET_FIELD] ??
      config[BookingOffsetUtil.RENT_OFFSET_LEGACY_FIELD];

    if (configured === null || configured === undefined || configured === "") {
      return 0;
    }

    const hours = Number(configured);
    return Number.isFinite(hours) ? hours : 0;
  }
}

export default BookingOffsetUtil;
